import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { dashboardStats, weeklyVisitors, categoryDistribution } from "../../../mocks/adminData";
import { allVenues } from "../../../mocks/allVenues";
import { campaigns } from "../../../mocks/campaigns";

interface RecentUser {
  id: string;
  username: string;
  display_name: string | null;
  email: string;
  joined_at: string;
  last_seen_at: string | null;
  avatar_url: string | null;
}

interface LiveStats {
  totalUsers: number;
  thisMonthUsers: number;
  activeNow: number;
  recentUsers: RecentUser[];
}

function TimeAgo({ date }: { date: string }) {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return <span className="text-emerald-600 font-medium">Az önce</span>;
  if (mins < 60) return <span>{mins} dk önce</span>;
  if (hours < 24) return <span>{hours} sa önce</span>;
  return <span>{days} gün önce</span>;
}

function LiveStatsSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-stone-200 flex-shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-stone-200 rounded w-2/3" />
            <div className="h-2.5 bg-stone-100 rounded w-1/2" />
          </div>
          <div className="h-2.5 bg-stone-100 rounded w-12" />
        </div>
      ))}
    </div>
  );
}

export default function DashboardSection() {
  const [liveStats, setLiveStats] = useState<LiveStats | null>(null);
  const [liveLoading, setLiveLoading] = useState(true);

  const sponsoredVenues = allVenues.filter((v) => v.isSponsored).slice(0, 5);

  const fetchLiveStats = useCallback(async () => {
    setLiveLoading(true);
    try {
      // Fetch all profiles with last_seen_at
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, username, display_name, email, joined_at, last_seen_at, avatar_url")
        .order("last_seen_at", { ascending: false });

      if (!profiles) {
        setLiveLoading(false);
        return;
      }

      const now = new Date();
      const thirtyMinsAgo = new Date(now.getTime() - 30 * 60 * 1000);
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const totalUsers = profiles.length;
      const thisMonthUsers = profiles.filter(
        (p) => p.joined_at && new Date(p.joined_at) > thirtyDaysAgo
      ).length;
      const activeNow = profiles.filter(
        (p) => p.last_seen_at && new Date(p.last_seen_at) > thirtyMinsAgo
      ).length;

      setLiveStats({
        totalUsers,
        thisMonthUsers,
        activeNow,
        recentUsers: profiles.slice(0, 8) as RecentUser[],
      });
    } catch {
      // silently fail
    }
    setLiveLoading(false);
  }, []);

  useEffect(() => {
    fetchLiveStats();
    // Refresh every 60 seconds
    const interval = setInterval(fetchLiveStats, 60000);
    return () => clearInterval(interval);
  }, [fetchLiveStats]);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0] || "")
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {dashboardStats.map((stat) => (
          <div key={stat.label} className="bg-white rounded-2xl p-4 border border-stone-100">
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl mb-3 ${stat.color}`}>
              <i className={`${stat.icon} text-base`} />
            </div>
            <div className="text-2xl font-bold text-stone-800 mb-0.5">{stat.value}</div>
            <div className="text-xs text-stone-400 mb-1">{stat.label}</div>
            <div className={`text-xs font-medium flex items-center gap-1 ${
              stat.trend === "up" ? "text-emerald-600" : stat.trend === "down" ? "text-red-500" : "text-stone-400"
            }`}>
              <i className={`text-xs ${stat.trend === "up" ? "ri-arrow-up-line" : stat.trend === "down" ? "ri-arrow-down-line" : "ri-subtract-line"}`} />
              {stat.change}
            </div>
          </div>
        ))}
      </div>

      {/* Live Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          {
            label: "Toplam Üye",
            value: liveLoading ? "..." : (liveStats?.totalUsers ?? 0),
            icon: "ri-user-line",
            color: "text-emerald-700 bg-emerald-50",
            badge: "Gerçek Zamanlı",
          },
          {
            label: "Bu Ay Katılan",
            value: liveLoading ? "..." : (liveStats?.thisMonthUsers ?? 0),
            icon: "ri-user-add-line",
            color: "text-amber-700 bg-amber-50",
            badge: "Son 30 Gün",
          },
          {
            label: "Aktif Oturum",
            value: liveLoading ? "..." : (liveStats?.activeNow ?? 0),
            icon: "ri-pulse-line",
            color: "text-emerald-700 bg-emerald-50",
            badge: "Son 30 Dk",
          },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-stone-100 p-5 flex items-center gap-4">
            <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${s.color}`}>
              <i className={`${s.icon} text-xl`} />
            </div>
            <div className="min-w-0">
              <p className="text-2xl font-bold text-stone-900">{s.value}</p>
              <p className="text-xs text-stone-500">{s.label}</p>
              <span className="inline-flex items-center gap-1 mt-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs text-emerald-600 font-medium">{s.badge}</span>
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Weekly Visitors Bar Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-5 border border-stone-100">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-semibold text-stone-800 text-sm">Haftalık Ziyaretçi</h3>
              <p className="text-xs text-stone-400 mt-0.5">Bu haftaki sayfa görüntülenme</p>
            </div>
            <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full">
              Toplam: 10.690
            </span>
          </div>
          <div className="flex items-end gap-3 h-36">
            {weeklyVisitors.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-stone-400 font-medium">{d.value.toLocaleString()}</span>
                <div className="w-full rounded-t-lg bg-emerald-100 relative overflow-hidden" style={{ height: "80px" }}>
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-emerald-600 rounded-t-lg transition-all duration-700"
                    style={{ height: `${d.pct}%` }}
                  />
                </div>
                <span className="text-xs text-stone-500">{d.day}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Category Distribution */}
        <div className="bg-white rounded-2xl p-5 border border-stone-100">
          <h3 className="font-semibold text-stone-800 text-sm mb-1">Kategori Dağılımı</h3>
          <p className="text-xs text-stone-400 mb-4">36 mekan dağılımı</p>
          <div className="space-y-3">
            {categoryDistribution.map((cat) => (
              <div key={cat.label}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-stone-600 font-medium">{cat.label}</span>
                  <span className="text-stone-400">{cat.count} mekan</span>
                </div>
                <div className="w-full bg-stone-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${cat.color} transition-all duration-700`}
                    style={{ width: `${cat.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Recent / Active Users — LIVE */}
        <div className="bg-white rounded-2xl p-5 border border-stone-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-stone-800 text-sm">Son Aktif Kullanıcılar</h3>
              <p className="text-xs text-stone-400 mt-0.5">Son giriş yapan üyeler</p>
            </div>
            <button
              onClick={fetchLiveStats}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-stone-400 hover:text-emerald-700 hover:bg-emerald-50 cursor-pointer transition-colors"
              title="Yenile"
            >
              <i className={`ri-refresh-line text-sm ${liveLoading ? "animate-spin" : ""}`} />
            </button>
          </div>

          {liveLoading ? (
            <LiveStatsSkeleton />
          ) : !liveStats || liveStats.recentUsers.length === 0 ? (
            <div className="text-center py-8 text-stone-400 text-sm">
              <i className="ri-user-line text-2xl mb-2 block" />
              Henüz kayıtlı üye yok
            </div>
          ) : (
            <div className="space-y-3">
              {liveStats.recentUsers.map((u) => {
                const displayName = u.display_name || u.username || u.email.split("@")[0];
                const isActive =
                  u.last_seen_at &&
                  new Date(u.last_seen_at) > new Date(Date.now() - 30 * 60 * 1000);
                return (
                  <div key={u.id} className="flex items-center gap-3">
                    {u.avatar_url ? (
                      <img
                        src={u.avatar_url}
                        alt={displayName}
                        className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-emerald-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {getInitials(displayName)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-stone-800 truncate">{displayName}</p>
                        {isActive && (
                          <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" title="Aktif" />
                        )}
                      </div>
                      <p className="text-xs text-stone-400 truncate">{u.email}</p>
                    </div>
                    <div className="text-xs text-stone-400 flex-shrink-0 text-right">
                      {u.last_seen_at ? <TimeAgo date={u.last_seen_at} /> : (
                        u.joined_at ? <TimeAgo date={u.joined_at} /> : "—"
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sponsored Venues Quick View */}
        <div className="bg-white rounded-2xl p-5 border border-stone-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-stone-800 text-sm">Sponsorlu Mekanlar</h3>
            <span className="text-xs text-stone-400">Aktif</span>
          </div>
          <div className="space-y-3">
            {sponsoredVenues.map((v) => (
              <div key={v.id} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
                  <img src={v.image} alt={v.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-800 truncate">{v.name}</p>
                  <p className="text-xs text-stone-400 truncate">{v.category} · {v.district}</p>
                </div>
                <div className="flex items-center gap-1 text-amber-500">
                  <i className="ri-star-fill text-xs" />
                  <span className="text-xs font-semibold text-stone-600">{v.rating}</span>
                </div>
              </div>
            ))}
          </div>
          {/* Quick stats */}
          <div className="mt-4 pt-4 border-t border-stone-100 grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-lg font-bold text-stone-800">{campaigns.length}</div>
              <div className="text-xs text-stone-400">Kampanya</div>
            </div>
            <div>
              <div className="text-lg font-bold text-stone-800">7</div>
              <div className="text-xs text-stone-400">Sponsorlu</div>
            </div>
            <div>
              <div className="text-lg font-bold text-stone-800">12</div>
              <div className="text-xs text-stone-400">Editör Seçimi</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
